import { BookingInfo } from "../types/container-unloading";

// Touch optimization utilities
export const hapticFeedback = () => {
  // Trigger haptic feedback on supported devices
  if ("vibrate" in navigator) {
    navigator.vibrate(10); // Light vibration
  }
};

export const preventZoom = (e: TouchEvent) => {
  // Prevent double-tap zoom on mobile
  if (e.touches.length > 1) {
    e.preventDefault();
  }
};

// Status calculation helpers
export const getBookingStepStatus = (booking: BookingInfo): number => {
  switch (booking.unloading_status) {
    case "pending":
      return 0;
    case "counting":
      return 0;
    case "delivery_ready":
      return 1;
    case "completed":
      return booking.has_delivery_address ? 2 : 1;
    default:
      return 0;
  }
};

export const getStatusColor = (
  status: BookingInfo["unloading_status"],
  hasDeliveryAddress?: boolean
): string => {
  switch (status) {
    case "pending":
      return "default";
    case "counting":
      return "processing";
    case "delivery_ready":
      return "warning"; // Orange - needs delivery setup
    case "completed":
      return hasDeliveryAddress ? "success" : "default"; // Green if shipped, gray if stored
    default:
      return "default";
  }
};

export const getStatusText = (booking: BookingInfo): string => {
  if (booking.unloading_status === "completed") {
    return booking.has_delivery_address ? "✓ Ship" : "Storage";
  }

  switch (booking.unloading_status) {
    case "pending":
      return "Not Started";
    case "counting":
      return "Counting";
    case "delivery_ready":
      return "Setup";
    default:
      return "Unknown";
  }
};

// Filter bookings based on search query
export const filterBookings = (
  bookingsToFilter: BookingInfo[],
  searchQuery: string,
  setExpandedBookings: React.Dispatch<React.SetStateAction<Set<number>>>
) => {
  if (!searchQuery.trim()) return bookingsToFilter;

  const query = searchQuery.toLowerCase();
  const filtered = bookingsToFilter.filter(
    (booking) =>
      // Search in booking reference
      booking.booking_reference.toLowerCase().includes(query) ||
      // Search in company name
      booking.company_name.toLowerCase().includes(query) ||
      // Search in products
      booking.products.some(
        (product) =>
          // Search in product name
          product.name.toLowerCase().includes(query) ||
          // Search in EAN
          (product.ean && product.ean.toString().includes(query))
      )
  );

  // Auto-expand bookings that have matching products when searching
  if (query.length > 2) {
    const bookingsWithMatchingProducts = filtered.filter((booking) =>
      booking.products.some(
        (product) =>
          product.name.toLowerCase().includes(query) ||
          (product.ean && product.ean.toString().includes(query))
      )
    );

    if (bookingsWithMatchingProducts.length > 0) {
      const expandIds = bookingsWithMatchingProducts.map((b) => b.id);
      setExpandedBookings((prev) => new Set([...prev, ...expandIds]));
    }
  }

  return filtered;
};

// Get bookings grouped by status
export const getBookingsByStatus = (
  bookings: BookingInfo[],
  activeTab: string,
  searchQuery: string,
  setExpandedBookings: React.Dispatch<React.SetStateAction<Set<number>>>
) => {
  let bookingsToDisplay: BookingInfo[] = [];

  if (activeTab === "shipping") {
    // Not in storage (delivery type bookings)
    bookingsToDisplay = bookings.filter((b) => b.delivery_type === "delivery");
  } else if (activeTab === "storage") {
    // In storage (storage type bookings)
    bookingsToDisplay = bookings.filter((b) => b.delivery_type === "storage");
  }

  // Apply search filter
  return filterBookings(bookingsToDisplay, searchQuery, setExpandedBookings);
};

// Check if all products in a booking are counted
export const areAllProductsCounted = (booking: BookingInfo): boolean => {
  return booking.products.every((p) => p.completed);
};

// Calculate overall progress
export const calculateProgress = (bookings: BookingInfo[]) => {
  const totalBookings = bookings.length;
  const completedBookings = bookings.filter(
    (b) => b.unloading_status === "completed"
  ).length;
  const progressPercent =
    totalBookings > 0 ? (completedBookings / totalBookings) * 100 : 0;

  return { totalBookings, completedBookings, progressPercent };
};
